import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import { motion, useScroll, useTransform } from "framer-motion";

// Icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StorageIcon from "@mui/icons-material/Storage";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

// Import your logo assets
import figLogo from "../../assets/logos/fig_CROP.png";
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

// Import the "Software Built for K12" section if you have it
import WhatWeBuild from "./WhatWeBuild";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GLASSMORPHISM DESIGN SYSTEM
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const glassCard = {
    background: "rgba(255, 255, 255, 0.12)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: 4,
    boxShadow: "0 8px 32px rgba(0, 51, 102, 0.12)",
};

const glassCardLight = {
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    border: "1px solid rgba(255, 255, 255, 0.5)",
    borderRadius: 4,
    boxShadow: "0 8px 32px rgba(0, 51, 102, 0.08)",
};

const glassCardAccent = {
    background: "rgba(33, 219, 184, 0.08)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(33, 219, 184, 0.2)",
    borderRadius: 4,
    boxShadow: "0 8px 32px rgba(33, 219, 184, 0.1)",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// HAPTIC ANIMATION PRESETS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const haptics = {
    tap: { scale: 0.96, transition: { type: "spring", stiffness: 500, damping: 20 } },
    hover: { scale: 1.02, y: -4, transition: { type: "spring", stiffness: 400, damping: 15 } },
    float: { y: [0, -8, 0], transition: { duration: 3, repeat: Infinity, ease: "easeInOut" } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 15 } },
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 100, damping: 15 } },
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// KEY DIFFERENTIATORS - What makes FIG unique
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const differentiators = [
    {
        icon: <SchoolIcon />,
        title: "Culture-Changing PD",
        description: "Transform your nutrition program with professional development that builds expertise, accountability, and a culture of excellence.",
        color: "#21dbb8",
    },
    {
        icon: <SmartToyIcon />,
        title: "AI-Powered Operations",
        description: "Invoice analysis, recipe development, form completion, and procurement insights—all automated.",
        color: "#9c27b0",
    },
    {
        icon: <HealthAndSafetyIcon />,
        title: "HIPAA Compliant",
        description: "Secure communication for special diets between families, nurses, and nutrition staff.",
        color: "#0090B3",
    },
    {
        icon: <StorageIcon />,
        title: "GDSN & FoodData Central",
        description: "Direct integration with global product data networks for accurate nutrition information.",
        color: "#145c8c",
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// FIG SUITE CAPABILITIES - Overview (details on Solutions page)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const figCapabilities = [
    { label: "Professional Development", icon: <SchoolIcon /> },
    { label: "Financial Management", icon: <TrendingUpIcon /> },
    { label: "Procurement Analysis", icon: <SmartToyIcon /> },
    { label: "Point of Sale", icon: <TouchAppIcon /> },
    { label: "Special Diets", icon: <HealthAndSafetyIcon /> },
    { label: "Buying Cooperative", icon: <GroupsIcon /> },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TESTIMONIALS DATA
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const testimonials = [
    {
        name: "Emily Thornton",
        title: "Thomasville City Schools",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/130d17b2-f8fe-4e84-ac3c-4bf0728bb5f8_20241213153031.png",
        quote: "FIG K-12 is a very efficient child nutrition professional development tool. I have found the FIG K-12 to be one of our school food service best purchases. The program streamlines the required school food service professional development requirements.",
    },
    {
        name: "Julie Fuerch",
        title: "Barker Central & Royalton Hartland Schools",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/bad62c6a-3b22-402d-911d-5e1edf7b9927_20241213152144.png",
        quote: "FIG K12 has made it much easier to onboard, train, and keep everyone up to date. It's become a critical part of our department's success. Training is now a continuous process that we can manage effectively.",
    },
    {
        name: "Quentin Cavanagh",
        title: "Anderson School District 5",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/4f6030b1-3f1b-4bd8-94b2-ce4b4700c24b_20241213153236.png",
        quote: "We are on our 2nd full school year with Swan Solutions and the FIG product and like the program very much. The support team has been responsive and open to suggestions for improvement.",
    },
    {
        name: "Scott Ziobrowski",
        title: "Hilton Central School District",
        image: "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/375f8a72-8783-4ce5-9af3-d0879d63b2f9_20241218115132.png",
        quote: "As a 14 year Food Service Director, it is important to streamline and utilize time management. FIG has allowed us to not only have a clean audit trail, but also maximize our time and focus more on supporting our students and staff.",
    },
];



// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN HOME COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function Home() {
    const { scrollYProgress } = useScroll();
    const heroOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0.9]);
    const heroScale = useTransform(scrollYProgress, [0, 0.12], [1, 0.98]);

    return (
        <Box sx={{ width: "100%", bgcolor: "background.default", overflow: "hidden" }}>
            {/* ═══════════════════════════════════════════════════════════════════════
                HERO SECTION - Clear Value Proposition
            ═══════════════════════════════════════════════════════════════════════ */}
            <Box
                component={motion.section}
                style={{ opacity: heroOpacity, scale: heroScale }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: { xs: 2, md: 4 },
                    py: { xs: 8, md: 12 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: `
                        radial-gradient(ellipse at 20% 0%, rgba(33, 219, 184, 0.25) 0%, transparent 50%),
                        radial-gradient(ellipse at 80% 100%, rgba(0, 119, 182, 0.3) 0%, transparent 50%),
                        linear-gradient(165deg, #0a1628 0%, #0d2847 40%, #134e6f 100%)
                    `,
                    minHeight: { xs: "auto", md: "75vh" },
                    position: "relative",
                    boxShadow: "0 4px 60px rgba(0, 51, 102, 0.2)",
                    mb: 0,
                }}
            >
                {/* Animated background orbs */}
                <Box
                    component={motion.div}
                    animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                    sx={{
                        position: "absolute",
                        width: 400,
                        height: 400,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(33, 219, 184, 0.15) 0%, transparent 70%)",
                        top: "5%",
                        left: "0%",
                        filter: "blur(60px)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    component={motion.div}
                    animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                    sx={{
                        position: "absolute",
                        width: 500,
                        height: 500,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(0, 119, 182, 0.2) 0%, transparent 70%)",
                        bottom: "-10%",
                        right: "-5%",
                        filter: "blur(80px)",
                        pointerEvents: "none",
                    }}
                />

                {/* Floating particles */}
                {[...Array(15)].map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{ y: [-15, 15], opacity: [0.3, 0.6, 0.3] }}
                        transition={{
                            duration: 3 + Math.random() * 3,
                            repeat: Infinity,
                            repeatType: "reverse",
                            delay: Math.random() * 2,
                        }}
                        style={{
                            position: "absolute",
                            left: `${10 + Math.random() * 80}%`,
                            top: `${10 + Math.random() * 80}%`,
                            width: 3 + Math.random() * 5,
                            height: 3 + Math.random() * 5,
                            borderRadius: "50%",
                            background: "rgba(255, 255, 255, 0.4)",
                        }}
                    />
                ))}

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    {/* Award Badge */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Chip
                            icon={<EmojiEventsIcon sx={{ fontSize: 18, color: "#d4af37 !important" }} />}
                            label="USFA DIRECTORS CHOICE 2025 — FIG Finance"
                            sx={{
                                mb: 3,
                                py: 2.5,
                                px: 2,
                                height: "auto",
                                ...glassCard,
                                background: "rgba(212, 175, 55, 0.15)",
                                border: "1px solid rgba(212, 175, 55, 0.4)",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: { xs: "0.75rem", md: "0.85rem" },
                                letterSpacing: "0.05em",
                                "& .MuiChip-label": { px: 1 },
                            }}
                        />
                    </motion.div>

                    {/* Logo */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <Box
                            component={motion.img}
                            src={swanSolutionsLogo}
                            alt="Swan Solutions Logo"
                            animate={haptics.float}
                            sx={{
                                height: { xs: 60, md: 90 },
                                mb: 3,
                                filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.3))",
                            }}
                        />
                    </motion.div>

                    {/* Main Headline */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: "2.4rem", sm: "3.2rem", md: "4.2rem" },
                                letterSpacing: "-0.03em",
                                lineHeight: 1.08,
                                mb: 2,
                                background: "linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.9) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            The Most Advanced
                            <br />
                            K12 Nutrition Software Suite
                        </Typography>
                    </motion.div>

                    {/* Subheadline */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                maxWidth: 700,
                                mx: "auto",
                                mb: 4,
                                fontWeight: 400,
                                fontSize: { xs: "1.05rem", md: "1.25rem" },
                                color: "rgba(255,255,255,0.88)",
                                lineHeight: 1.65,
                            }}
                        >
                            From palm-scanning POS to AI-powered procurement analysis—FIG is the complete platform
                            for school nutrition finance, compliance, training, and operations.
                        </Typography>
                    </motion.div>

                    {/* CTA Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Button
                                component={motion.a}
                                href="/solutions"
                                whileHover={haptics.hover}
                                whileTap={haptics.tap}
                                size="large"
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    background: "linear-gradient(135deg, #21dbb8 0%, #00B4D8 100%)",
                                    color: "#003366",
                                    fontSize: "1.05rem",
                                    fontWeight: 700,
                                    py: 1.75,
                                    px: 4.5,
                                    borderRadius: 3,
                                    boxShadow: "0 6px 24px rgba(33, 219, 184, 0.4)",
                                    "&:hover": {
                                        background: "linear-gradient(135deg, #2ee8c5 0%, #00c4e8 100%)",
                                    },
                                }}
                            >
                                Explore the FIG Suite
                            </Button>
                            <Button
                                component={motion.a}
                                href="/contact"
                                whileHover={{ scale: 1.02 }}
                                whileTap={haptics.tap}
                                size="large"
                                variant="outlined"
                                startIcon={<PlayArrowIcon />}
                                sx={{
                                    ...glassCard,
                                    color: "#fff",
                                    borderColor: "rgba(255,255,255,0.35)",
                                    fontSize: "1.05rem",
                                    fontWeight: 600,
                                    py: 1.75,
                                    px: 4.5,
                                    borderRadius: 3,
                                    "&:hover": {
                                        borderColor: "rgba(255,255,255,0.6)",
                                        background: "rgba(255,255,255,0.12)",
                                    },
                                }}
                            >
                                Request a Demo
                            </Button>
                        </Stack>
                    </motion.div>

                    {/* Quick Capability Pills */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            flexWrap="wrap"
                            sx={{ mt: 5, gap: 1 }}
                        >
                            {figCapabilities.map((cap, i) => (
                                <motion.div
                                    key={cap.label}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.7 + i * 0.08 }}
                                >
                                    <Chip
                                        icon={React.cloneElement(cap.icon, {
                                            sx: { fontSize: 16, color: "rgba(255,255,255,0.8) !important" },
                                        })}
                                        label={cap.label}
                                        size="small"
                                        sx={{
                                            background: "rgba(255, 255, 255, 0.1)",
                                            border: "1px solid rgba(255, 255, 255, 0.2)",
                                            color: "rgba(255,255,255,0.9)",
                                            fontWeight: 500,
                                            fontSize: "0.8rem",
                                            py: 2,
                                            "& .MuiChip-label": { px: 1 },
                                        }}
                                    />
                                </motion.div>
                            ))}
                        </Stack>
                    </motion.div>
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════════
                THE FIG SUITE - Unified Platform Overview
            ═══════════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                    background: `
                        radial-gradient(ellipse at 0% 50%, rgba(33, 219, 184, 0.06) 0%, transparent 50%),
                        radial-gradient(ellipse at 100% 50%, rgba(0, 119, 182, 0.06) 0%, transparent 50%),
                        linear-gradient(180deg, transparent 0%, rgba(0, 51, 102, 0.02) 100%)
                    `,
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={staggerContainer}
                    >
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <motion.div variants={fadeInUp}>
                                    <Chip
                                        label="THE FIG SUITE"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                            letterSpacing: "0.1em",
                                            background: "rgba(33, 219, 184, 0.15)",
                                            color: "#0d9488",
                                            border: "1px solid rgba(33, 219, 184, 0.3)",
                                        }}
                                    />
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: { xs: "2rem", md: "2.6rem" },
                                            lineHeight: 1.15,
                                            mb: 3,
                                        }}
                                    >
                                        One Integrated Platform.
                                        <br />
                                        <Box
                                            component="span"
                                            sx={{
                                                background: "linear-gradient(135deg, #21dbb8 0%, #0077B6 100%)",
                                                WebkitBackgroundClip: "text",
                                                WebkitTextFillColor: "transparent",
                                            }}
                                        >
                                            Complete Nutrition Operations.
                                        </Box>
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "1.1rem",
                                            lineHeight: 1.75,
                                            color: "text.secondary",
                                            mb: 3,
                                        }}
                                    >
                                        FIG connects every aspect of your nutrition program—from staff training and
                                        financial management to point-of-sale and special diets. No more juggling
                                        disconnected systems. No more manual data transfers.
                                    </Typography>

                                    <Stack spacing={1.5} sx={{ mb: 4 }}>
                                        {[
                                            "Unified data across all modules",
                                            "Real-time reporting and dashboards",
                                            "Seamless district-wide deployment",
                                            "Enterprise-grade security",
                                        ].map((item, i) => (
                                            <Box
                                                key={i}
                                                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                                            >
                                                <CheckCircleIcon sx={{ color: "#21dbb8", fontSize: 22 }} />
                                                <Typography sx={{ fontWeight: 500 }}>{item}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>

                                    <Button
                                        component={motion.a}
                                        href="/solutions"
                                        whileHover={haptics.hover}
                                        whileTap={haptics.tap}
                                        size="large"
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{
                                            background: "linear-gradient(135deg, #003366 0%, #0077B6 100%)",
                                            color: "#fff",
                                            fontWeight: 700,
                                            py: 1.5,
                                            px: 4,
                                            borderRadius: 3,
                                            boxShadow: "0 4px 20px rgba(0, 51, 102, 0.25)",
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #002244 0%, #005588 100%)",
                                            },
                                        }}
                                    >
                                        See All Solutions
                                    </Button>
                                </motion.div>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <motion.div variants={scaleIn}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        {/* Central FIG Logo */}
                                        <Box
                                            component={motion.div}
                                            animate={haptics.float}
                                            sx={{
                                                ...glassCardLight,
                                                p: 4,
                                                borderRadius: 5,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                boxShadow: "0 12px 48px rgba(33, 219, 184, 0.15)",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={figLogo}
                                                alt="FIG Platform"
                                                sx={{
                                                    width: { xs: 100, md: 140 },
                                                    height: "auto",
                                                    mb: 2,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: "1.3rem",
                                                    color: "#003366",
                                                    mb: 1,
                                                }}
                                            >
                                                FIG Platform
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.9rem",
                                                    color: "text.secondary",
                                                    textAlign: "center",
                                                }}
                                            >
                                                Complete K12 Nutrition Suite
                                            </Typography>

                                            {/* Module badges around the logo */}
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                flexWrap="wrap"
                                                justifyContent="center"
                                                sx={{ mt: 3, gap: 1 }}
                                            >
                                                {["PD", "Finance", "Procurement", "POS", "Diets", "Co-op"].map(
                                                    (module) => (
                                                        <Chip
                                                            key={module}
                                                            label={module}
                                                            size="small"
                                                            sx={{
                                                                background: "rgba(33, 219, 184, 0.12)",
                                                                border: "1px solid rgba(33, 219, 184, 0.25)",
                                                                fontWeight: 600,
                                                                fontSize: "0.7rem",
                                                            }}
                                                        />
                                                    )
                                                )}
                                            </Stack>
                                        </Box>
                                    </Box>
                                </motion.div>
                            </Grid>
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════════
                KEY DIFFERENTIATORS - What Makes FIG Unique
            ═══════════════════════════════════════════════════════════════════════ */}
            <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "background.default" }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={staggerContainer}
                    >
                        <Box sx={{ textAlign: "center", mb: 6 }}>
                            <motion.div variants={fadeInUp}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "#21dbb8",
                                        fontWeight: 700,
                                        letterSpacing: "0.15em",
                                        mb: 1,
                                        display: "block",
                                    }}
                                >
                                    Why Districts Choose FIG
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: "2rem", md: "2.8rem" },
                                        mb: 2,
                                        background: "linear-gradient(135deg, #003366 0%, #0077B6 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                    }}
                                >
                                    Technology That Sets Us Apart
                                </Typography>
                                <Typography
                                    sx={{
                                        maxWidth: 600,
                                        mx: "auto",
                                        color: "text.secondary",
                                        fontSize: "1.1rem",
                                        lineHeight: 1.7,
                                    }}
                                >
                                    We don't just digitize your processes—we transform them with capabilities
                                    no other K12 nutrition platform offers.
                                </Typography>
                            </motion.div>
                        </Box>

                        <Grid container spacing={3}>
                            {differentiators.map((diff, i) => (
                                <Grid item xs={12} sm={6} md={3} key={diff.title}>
                                    <motion.div variants={fadeInUp}>
                                        <Card
                                            component={motion.div}
                                            whileHover={haptics.hover}
                                            whileTap={haptics.tap}
                                            sx={{
                                                ...glassCardLight,
                                                height: "100%",
                                                p: 3,
                                                textAlign: "center",
                                                cursor: "pointer",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    boxShadow: `0 16px 48px ${diff.color}25`,
                                                    borderColor: `${diff.color}40`,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: 3,
                                                    background: `linear-gradient(135deg, ${diff.color}20 0%, ${diff.color}40 100%)`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    mx: "auto",
                                                    mb: 2,
                                                    "& svg": { fontSize: 32, color: diff.color },
                                                }}
                                            >
                                                {diff.icon}
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 700, mb: 1, fontSize: "1.1rem" }}
                                            >
                                                {diff.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "text.secondary", lineHeight: 1.6 }}
                                            >
                                                {diff.description}
                                            </Typography>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════════
                WHO WE SERVE
            ═══════════════════════════════════════════════════════════════════════ */}
            <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "background.default" }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={staggerContainer}
                    >
                        <Box sx={{ textAlign: "center", mb: 6 }}>
                            <motion.div variants={fadeInUp}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "#0077B6",
                                        fontWeight: 700,
                                        letterSpacing: "0.15em",
                                        display: "block",
                                        mb: 1,
                                    }}
                                >
                                    Who We Serve
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: "2rem", md: "2.6rem" },
                                        mb: 2,
                                    }}
                                >
                                    Built by K12 Experts, For K12
                                </Typography>
                            </motion.div>
                        </Box>

                        <Grid container spacing={4}>
                            {[
                                {
                                    title: "School Districts",
                                    description:
                                        "From small rural districts to the largest urban systems, we scale with you. Our platform handles complexity while keeping operations simple.",
                                    icon: <SchoolIcon />,
                                },
                                {
                                    title: "State Agencies",
                                    description:
                                        "Aggregate visibility across districts, standardized compliance reporting, and tools to support your program oversight responsibilities.",
                                    icon: <AccountBalanceIcon />,
                                },
                                {
                                    title: "EdTech & Vendors",
                                    description:
                                        "Partner with us on integrations, co-development, or white-label solutions. We build the backend so you can focus on your core product.",
                                    icon: <GroupsIcon />,
                                },
                            ].map((item, i) => (
                                <Grid item xs={12} md={4} key={item.title}>
                                    <motion.div variants={fadeInUp}>
                                        <Card
                                            component={motion.div}
                                            whileHover={{ y: -6 }}
                                            sx={{
                                                ...glassCardLight,
                                                p: 4,
                                                height: "100%",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    boxShadow: "0 16px 48px rgba(0, 119, 182, 0.12)",
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: 3,
                                                    background: "linear-gradient(135deg, #003366 0%, #0077B6 100%)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    mb: 3,
                                                    "& svg": { fontSize: 28, color: "#fff" },
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                sx={{ fontWeight: 700, mb: 2, fontSize: "1.3rem" }}
                                            >
                                                {item.title}
                                            </Typography>
                                            <Typography
                                                sx={{ color: "text.secondary", lineHeight: 1.7 }}
                                            >
                                                {item.description}
                                            </Typography>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════════
                TESTIMONIALS - Social Proof
            ═══════════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                    background: `
                        radial-gradient(ellipse at 0% 50%, rgba(33, 219, 184, 0.06) 0%, transparent 50%),
                        radial-gradient(ellipse at 100% 50%, rgba(0, 119, 182, 0.06) 0%, transparent 50%),
                        linear-gradient(180deg, transparent 0%, rgba(0, 51, 102, 0.02) 100%)
                    `,
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={staggerContainer}
                    >
                        <Box sx={{ textAlign: "center", mb: 6 }}>
                            <motion.div variants={fadeInUp}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "#21dbb8",
                                        fontWeight: 700,
                                        letterSpacing: "0.15em",
                                        display: "block",
                                        mb: 1,
                                    }}
                                >
                                    Trusted by K12 Leaders
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: "2rem", md: "2.6rem" },
                                        mb: 2,
                                        background: "linear-gradient(135deg, #003366 0%, #0077B6 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                    }}
                                >
                                    Real Impact, Real Voices
                                </Typography>
                            </motion.div>
                        </Box>

                        <Grid container spacing={3}>
                            {testimonials.map((testimonial, i) => (
                                <Grid item xs={12} sm={6} md={3} key={testimonial.name}>
                                    <motion.div variants={fadeInUp}>
                                        <Card
                                            component={motion.div}
                                            whileHover={{ y: -8, scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            sx={{
                                                height: "100%",
                                                p: 3,
                                                borderRadius: 4,
                                                background: "rgba(255, 255, 255, 0.65)",
                                                backdropFilter: "blur(24px)",
                                                WebkitBackdropFilter: "blur(24px)",
                                                border: "1px solid rgba(255, 255, 255, 0.5)",
                                                boxShadow: "0 8px 32px rgba(0, 51, 102, 0.08)",
                                                transition: "all 0.3s ease",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                textAlign: "center",
                                                "&:hover": {
                                                    boxShadow: "0 20px 60px rgba(33, 219, 184, 0.18)",
                                                    border: "1px solid rgba(33, 219, 184, 0.35)",
                                                },
                                            }}
                                        >
                                            {/* Large Profile Image */}
                                            <Box
                                                component="img"
                                                src={testimonial.image}
                                                alt={testimonial.name}
                                                sx={{
                                                    width: { xs: 80, md: 100 },
                                                    height: { xs: 80, md: 100 },
                                                    borderRadius: "50%",
                                                    objectFit: "cover",
                                                    border: "4px solid #21dbb8",
                                                    boxShadow: "0 4px 20px rgba(33, 219, 184, 0.3)",
                                                    mb: 2,
                                                }}
                                            />

                                            {/* Author Name & Title */}
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: "1rem",
                                                    color: "#003366",
                                                    mb: 0.5,
                                                }}
                                            >
                                                {testimonial.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    color: "#0077B6",
                                                    fontWeight: 500,
                                                    mb: 2,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {testimonial.title}
                                            </Typography>

                                            {/* Quote */}
                                            <Box
                                                sx={{
                                                    color: "#21dbb8",
                                                    fontSize: "2rem",
                                                    fontFamily: "Georgia, serif",
                                                    lineHeight: 0.5,
                                                    mb: 1,
                                                    opacity: 0.5,
                                                }}
                                            >
                                                "
                                            </Box>
                                            <Typography
                                                sx={{
                                                    color: "text.secondary",
                                                    fontSize: "0.85rem",
                                                    lineHeight: 1.65,
                                                    fontStyle: "italic",
                                                    flex: 1,
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 4,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {testimonial.quote}
                                            </Typography>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>

                        {/* View All Link */}
                        <Box sx={{ textAlign: "center", mt: 6 }}>
                            <Button
                                component={motion.a}
                                href="/testimonials"
                                whileHover={{ scale: 1.03, x: 4 }}
                                whileTap={{ scale: 0.97 }}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "1.05rem",
                                    color: "#003366",
                                    py: 1.5,
                                    px: 3,
                                    borderRadius: 3,
                                    background: "rgba(255, 255, 255, 0.7)",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid rgba(0, 51, 102, 0.15)",
                                    "&:hover": {
                                        background: "rgba(33, 219, 184, 0.1)",
                                        borderColor: "rgba(33, 219, 184, 0.3)",
                                    },
                                }}
                            >
                                Read More Testimonials
                            </Button>
                        </Box>
                    </motion.div>
                </Container>
            </Box>

            {/* What We Build Section - if you have it */}
            {WhatWeBuild && <WhatWeBuild />}

            {/* ═══════════════════════════════════════════════════════════════════════
                SWAN AI LABS CTA
            ═══════════════════════════════════════════════════════════════════════ */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6 }}
                >
                    <Card
                        component={motion.div}
                        whileHover={{ y: -6 }}
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: "center",
                            p: { xs: 4, md: 6 },
                            borderRadius: 5,
                            ...glassCardAccent,
                            transition: "all 0.4s ease",
                            "&:hover": {
                                boxShadow: "0 20px 60px rgba(33, 219, 184, 0.18)",
                            },
                        }}
                    >
                        <Box
                            component={motion.img}
                            src={swanAiLabsLogo}
                            alt="Swan AI Labs"
                            animate={haptics.float}
                            sx={{
                                width: { xs: 120, md: 150 },
                                height: "auto",
                                mb: { xs: 3, md: 0 },
                                mr: { md: 5 },
                                borderRadius: 3,
                                boxShadow: "0 4px 24px rgba(0, 51, 102, 0.12)",
                                bgcolor: "#fff",
                                p: 2,
                            }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: "#21dbb8",
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    display: "block",
                                    mb: 0.5,
                                }}
                            >
                                Beyond the Platform
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    mb: 2,
                                    fontSize: { xs: "1.5rem", md: "2rem" },
                                    background: "linear-gradient(135deg, #0d5c63 0%, #21dbb8 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                Swan AI Labs & Custom Development
                            </Typography>
                            <Typography
                                sx={{
                                    mb: 3,
                                    color: "text.secondary",
                                    fontSize: "1.08rem",
                                    lineHeight: 1.7,
                                    maxWidth: 600,
                                }}
                            >
                                Need something the FIG platform doesn't cover? Our AI Labs team builds custom
                                solutions—from predictive analytics to workflow automation—tailored to your
                                district's unique challenges.
                            </Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <Button
                                    component={motion.a}
                                    href="/labs"
                                    whileHover={haptics.hover}
                                    whileTap={haptics.tap}
                                    variant="contained"
                                    size="large"
                                    endIcon={<AutoAwesomeIcon />}
                                    sx={{
                                        background: "linear-gradient(135deg, #21dbb8 0%, #0d9488 100%)",
                                        color: "#fff",
                                        fontWeight: 700,
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        boxShadow: "0 4px 20px rgba(33, 219, 184, 0.3)",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #2ee8c5 0%, #10b981 100%)",
                                        },
                                    }}
                                >
                                    Explore AI Labs
                                </Button>
                                <Button
                                    component={motion.a}
                                    href="/contact"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={haptics.tap}
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        borderColor: "#003366",
                                        color: "#003366",
                                        fontWeight: 600,
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        borderWidth: 2,
                                        "&:hover": {
                                            borderWidth: 2,
                                            background: "rgba(0, 51, 102, 0.04)",
                                        },
                                    }}
                                >
                                    Discuss a Custom Project
                                </Button>
                            </Stack>
                        </Box>
                    </Card>
                </motion.div>
            </Container>

            {/* ═══════════════════════════════════════════════════════════════════════
                FINAL CTA
            ═══════════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    py: { xs: 10, md: 14 },
                    background: "linear-gradient(165deg, #0a1628 0%, #0d2847 40%, #134e6f 100%)",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Background orb */}
                <Box
                    component={motion.div}
                    animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 8, repeat: Infinity }}
                    sx={{
                        position: "absolute",
                        width: 600,
                        height: 600,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(33, 219, 184, 0.2) 0%, transparent 70%)",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        filter: "blur(80px)",
                        pointerEvents: "none",
                    }}
                />

                <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Box sx={{ textAlign: "center" }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: "2rem", md: "3rem" },
                                    color: "#fff",
                                    mb: 3,
                                    lineHeight: 1.15,
                                }}
                            >
                                Ready to Transform Your
                                <br />
                                <Box
                                    component="span"
                                    sx={{
                                        background: "linear-gradient(135deg, #21dbb8 0%, #00B4D8 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                    }}
                                >
                                    Nutrition Program?
                                </Box>
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "1.15rem",
                                    color: "rgba(255,255,255,0.85)",
                                    maxWidth: 600,
                                    mx: "auto",
                                    mb: 4,
                                    lineHeight: 1.7,
                                }}
                            >
                                Join hundreds of districts who've modernized their operations with FIG.
                                Let's discuss how we can help your program thrive.
                            </Typography>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                justifyContent="center"
                            >
                                <Button
                                    component={motion.a}
                                    href="/contact"
                                    whileHover={haptics.hover}
                                    whileTap={haptics.tap}
                                    size="large"
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        background: "linear-gradient(135deg, #21dbb8 0%, #00B4D8 100%)",
                                        color: "#003366",
                                        fontSize: "1.1rem",
                                        fontWeight: 700,
                                        py: 2,
                                        px: 5,
                                        borderRadius: 3,
                                        boxShadow: "0 6px 28px rgba(33, 219, 184, 0.4)",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #2ee8c5 0%, #00c4e8 100%)",
                                        },
                                    }}
                                >
                                    Schedule a Demo
                                </Button>
                                <Button
                                    component={motion.a}
                                    href="/solutions"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={haptics.tap}
                                    size="large"
                                    variant="outlined"
                                    sx={{
                                        color: "#fff",
                                        borderColor: "rgba(255,255,255,0.4)",
                                        fontSize: "1.1rem",
                                        fontWeight: 600,
                                        py: 2,
                                        px: 5,
                                        borderRadius: 3,
                                        borderWidth: 2,
                                        "&:hover": {
                                            borderWidth: 2,
                                            borderColor: "rgba(255,255,255,0.7)",
                                            background: "rgba(255,255,255,0.08)",
                                        },
                                    }}
                                >
                                    View All Solutions
                                </Button>
                            </Stack>
                        </Box>
                    </motion.div>
                </Container>
            </Box>
        </Box>
    );
}